import { db, storage, auth } from '../firebase';
import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

// Feedback & bug report store (handoff 14 §B4). App-native: reports land in the
// Firestore `feedback` collection (admin-only read/update/delete; owner create,
// see firestore.rules) and attachment binaries go to Storage under
// `feedback/{id}/` out-of-line, keeping only {name,type,size,path,url} on the
// record. RB does not receive app feedback.
//
// Payload shape mirrors the RB-UX reference serve.py /feedback:
//   { kind, severity, summary, detail, expected, page, diagnostics, attachments }
// where each attachment is { name, type, size, dataUrl }.

// Collect the auto-diagnostics attached to every report so the team can
// reproduce it (browser / OS / viewport / screen+DPR / theme / tz / language /
// page). Ported from the prototype shell.js fbCollectDiag + fbParseUA.
export function collectDiagnostics(page) {
  const n = typeof navigator !== 'undefined' ? navigator : {};
  const ua = n.userAgent || '';
  const uad = n.userAgentData;
  let os = (uad && uad.platform) || 'Unknown';
  let m;
  if (/Windows NT 10/.test(ua)) os = 'Windows 10/11';
  else if ((m = ua.match(/Mac OS X ([0-9_]+)/))) os = `macOS ${m[1].replace(/_/g, '.')}`;
  else if ((m = ua.match(/Android ([0-9.]+)/))) os = `Android ${m[1]}`;
  else if ((m = ua.match(/(?:iPhone|iPad); CPU.*?OS ([0-9_]+)/))) os = `iOS ${m[1].replace(/_/g, '.')}`;
  else if (/Linux/.test(ua)) os = 'Linux';

  let browser = 'Unknown';
  if ((m = ua.match(/Edg\/([0-9.]+)/))) browser = `Edge ${m[1]}`;
  else if ((m = ua.match(/OPR\/([0-9.]+)/))) browser = `Opera ${m[1]}`;
  else if ((m = ua.match(/Chrome\/([0-9.]+)/))) browser = `Chrome ${m[1]}`;
  else if ((m = ua.match(/Firefox\/([0-9.]+)/))) browser = `Firefox ${m[1]}`;
  else if ((m = ua.match(/Version\/([0-9.]+).*Safari/))) browser = `Safari ${m[1]}`;

  let tz = '';
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) {}
  const dark = typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;

  return {
    browser,
    os,
    viewport: `${window.innerWidth}\u00d7${window.innerHeight}`,
    screen: `${window.screen.width}\u00d7${window.screen.height}`,
    dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
    colorScheme: dark ? 'dark' : 'light',
    language: n.language || '',
    timezone: tz,
    page: page || '',
    url: window.location.href,
    ua,
  };
}

/**
 * Persist a feedback report. Uploads each attachment to Storage, then writes the
 * record (with attachment metadata) to Firestore.
 *
 * @param {object} report { kind, severity, summary, detail, expected, page,
 *   diagnostics, attachments: [{ name, type, size, dataUrl }] }
 * @returns {Promise<{ id: string, attachments: Array }>}
 */
export async function submitFeedback(report) {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to send feedback.');

  const docRef = doc(collection(db, 'feedback'));
  const id = docRef.id;

  // Upload attachment binaries out-of-line; keep only metadata on the record.
  const incoming = Array.isArray(report.attachments) ? report.attachments : [];
  const stored = [];
  for (const a of incoming) {
    if (!a || !a.dataUrl) continue;
    // Sanitise the name so it is safe as a Storage object path segment.
    const safeName = String(a.name || 'attachment').replace(/[^\w.-]+/g, '_').slice(0, 120) || 'attachment';
    const path = `feedback/${id}/${safeName}`;
    try {
      const sref = ref(storage, path);
      await uploadString(sref, a.dataUrl, 'data_url', { contentType: a.type || 'application/octet-stream' });
      let url = '';
      try { url = await getDownloadURL(sref); } catch (_) { /* URL is best-effort; admins can read via path */ }
      stored.push({ name: safeName, type: a.type || '', size: a.size || 0, path, url });
    } catch (err) {
      // Keep the metadata so the report still records that a file was intended,
      // rather than dropping the whole submission for one failed upload.
      console.error('[feedback] attachment upload failed:', safeName, err);
      stored.push({ name: safeName, type: a.type || '', size: a.size || 0, path: '', url: '', error: String(err?.message || err) });
    }
  }

  const record = {
    id,
    kind: report.kind || 'other',
    severity: report.severity || 'medium',
    summary: String(report.summary || '').slice(0, 240),
    detail: String(report.detail || '').slice(0, 5000),
    expected: String(report.expected || '').slice(0, 5000),
    page: report.page || '',
    diagnostics: report.diagnostics || {},
    attachments: stored,
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || '',
    createdAt: serverTimestamp(),
  };

  try {
    await setDoc(docRef, record);
  } catch (err) {
    console.error('[feedback] Firestore write failed:', err);
    throw err;
  }

  return { id, attachments: stored };
}
